// Profile inspector — "what is this agent configuration, exactly?"
//
// A right-docked panel that fetches the injected-context manifest for the
// selected console profile (GET .../profiles/{id}/manifest) and shows EVERYTHING
// that shapes the agent: the resolved system prompt, the standing-context docs
// woven into it, an honest accounting of context that is NOT injected
// (CLAUDE.md, init scripts), the static Claude Agent SDK config, and the full
// tool / MCP surface — each tool with its description and JSON-Schema input.
//
// The content is shown verbatim (in <pre>), not rendered, because the point of
// an inspector is to see exactly the bytes the agent receives.

import { useEffect, useMemo, useState } from 'react';
import { Modal } from '../common/Modal';
import { DataAccessReportView } from './DataAccessPanel';
import {
  getProfileManifest,
  type ContextManifest,
  type InjectedDoc,
  type ToolSchema,
  type ToolSource,
} from '../../services/agentConsole';
import styles from './ProfileInspectorPanel.module.css';

const SOURCE_LABEL: Record<ToolSource, string> = {
  in_process: 'in-process',
  kb_mount: 'KB mount',
  sdk_builtin: 'SDK built-in',
};

const SOURCE_ORDER: ToolSource[] = ['in_process', 'kb_mount', 'sdk_builtin'];

const DOC_KIND_LABEL: Record<string, string> = {
  system_prompt: 'system prompt',
  skill: 'skill',
  claude_md: 'CLAUDE.md',
  settings: 'settings',
  init_script: 'init scripts',
};

function Collapsible({
  title,
  meta,
  defaultOpen = false,
  children,
}: {
  title: React.ReactNode;
  meta?: React.ReactNode;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={styles.group}>
      <button
        type="button"
        className={styles.groupHeader}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className={`${styles.caret} ${open ? styles.caretOpen : ''}`}>▸</span>
        <span className={styles.groupTitle}>{title}</span>
        {meta}
      </button>
      {open && <div className={styles.groupBody}>{children}</div>}
    </div>
  );
}

function DocBlock({ doc }: { doc: InjectedDoc }) {
  const kindLabel = DOC_KIND_LABEL[doc.kind] ?? doc.kind;
  return (
    <Collapsible
      // The injected prompt + skill default open (they're the substance); the
      // not-injected accountings start collapsed.
      defaultOpen={doc.present && doc.kind !== 'claude_md'}
      title={<span className={styles.docTitle}>{doc.title}</span>}
      meta={
        <>
          <span className={styles.kindChip}>{kindLabel}</span>
          <span
            className={doc.present ? styles.injectedBadge : styles.absentBadge}
          >
            {doc.present ? 'injected' : 'not injected'}
          </span>
        </>
      }
    >
      {doc.source_path && (
        <p className={styles.sourcePath}>
          <code>{doc.source_path}</code>
        </p>
      )}
      {doc.note && <p className={styles.note}>{doc.note}</p>}
      {doc.present && doc.content && (
        <pre className={styles.pre}>{doc.content}</pre>
      )}
    </Collapsible>
  );
}

function ToolBlock({ tool }: { tool: ToolSchema }) {
  const schemaText = tool.input_schema
    ? JSON.stringify(tool.input_schema, null, 2)
    : null;
  return (
    <Collapsible
      title={<code className={styles.toolName}>{tool.name}</code>}
      meta={
        <>
          {tool.writes && <span className={styles.writeBadge}>✍ write</span>}
          <span className={styles.sourceChip}>{SOURCE_LABEL[tool.source]}</span>
        </>
      }
    >
      <p className={styles.toolQualified}>
        <code>{tool.qualified_name}</code>
      </p>
      <p className={styles.toolDescription}>{tool.description}</p>
      {schemaText ? (
        <>
          <div className={styles.subLabel}>Input schema</div>
          <pre className={styles.pre}>{schemaText}</pre>
        </>
      ) : (
        <p className={styles.note}>
          Schema served by{' '}
          {tool.source === 'kb_mount'
            ? 'the external Knowledge Bank MCP server'
            : 'the Claude Agent SDK runtime'}{' '}
          (not defined in this codebase).
        </p>
      )}
    </Collapsible>
  );
}

function ConfigRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className={styles.kvRow}>
      <span className={styles.kvKey}>{label}</span>
      <span className={styles.kvValue}>{value}</span>
    </div>
  );
}

export function ProfileInspectorPanel({
  open,
  onClose,
  profileId,
}: {
  open: boolean;
  onClose: () => void;
  profileId: string;
}) {
  const [manifest, setManifest] = useState<ContextManifest | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // (Re)fetch whenever the panel opens or the inspected profile changes.
  useEffect(() => {
    if (!open || !profileId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setManifest(null);
    getProfileManifest(profileId)
      .then((m) => {
        if (!cancelled) setManifest(m);
      })
      .catch((err) => {
        if (!cancelled)
          setError(err instanceof Error ? err.message : 'Failed to load manifest');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, profileId]);

  const toolsBySource = useMemo(() => {
    const groups = new Map<ToolSource, ToolSchema[]>();
    for (const tool of manifest?.tools ?? []) {
      const list = groups.get(tool.source) ?? [];
      list.push(tool);
      groups.set(tool.source, list);
    }
    return groups;
  }, [manifest]);

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title={manifest ? `Inspect: ${manifest.profile_name}` : 'Inspect configuration'}
      size="lg"
    >
      <p className={styles.panelIntro}>
        Everything injected into and governing this agent configuration — the
        resolved prompt, standing-context docs, SDK config, the full tool/MCP
        schema, and the live table-level data access. The developer view.
      </p>

      <div className={styles.panelBody}>
        {loading && <p className={styles.statusLine}>Loading manifest…</p>}
        {error && (
          <p className={`${styles.statusLine} ${styles.statusError}`}>{error}</p>
        )}

        {manifest && (
          <>
            <p className={styles.profileDescription}>
              {manifest.profile_description}
            </p>

            <section className={styles.section}>
              <div className={styles.sectionHeader}>
                <h4>Configuration</h4>
                <span
                  className={manifest.writes ? styles.writeBadge : styles.roBadge}
                >
                  {manifest.writes ? 'reads + writes' : 'read-only'}
                </span>
              </div>
              <div className={styles.kvList}>
                <ConfigRow label="model" value={<code>{manifest.sdk.model}</code>} />
                <ConfigRow label="role" value={manifest.prompt_kind} />
                <ConfigRow
                  label="permission"
                  value={<code>{manifest.sdk.permission_mode}</code>}
                />
                <ConfigRow
                  label="MCP servers"
                  value={manifest.sdk.mcp_servers.map((s) => (
                    <code key={s} className={styles.inlineCode}>
                      {s}
                    </code>
                  ))}
                />
                <ConfigRow
                  label="setting sources"
                  value={manifest.sdk.setting_sources.join(', ') || '—'}
                />
                <ConfigRow
                  label="max thinking"
                  value={`${manifest.sdk.max_thinking_tokens.toLocaleString()} tokens`}
                />
                <ConfigRow label="cwd" value={manifest.sdk.cwd_strategy} />
                <ConfigRow
                  label="standing context"
                  value={manifest.sdk.standing_context_strategy}
                />
              </div>
            </section>

            <section className={styles.section}>
              <div className={styles.sectionHeader}>
                <h4>Injected context</h4>
                <span className={styles.count}>
                  {manifest.injected_docs.filter((d) => d.present).length} injected
                </span>
              </div>
              {manifest.injected_docs.map((doc, i) => (
                <DocBlock key={`${doc.kind}-${i}`} doc={doc} />
              ))}
            </section>

            <section className={styles.section}>
              <div className={styles.sectionHeader}>
                <h4>Tools &amp; MCP</h4>
                <span className={styles.count}>{manifest.tools.length}</span>
              </div>
              {SOURCE_ORDER.filter((s) => toolsBySource.has(s)).map((source) => (
                <div key={source} className={styles.toolGroup}>
                  <div className={styles.toolGroupLabel}>
                    {SOURCE_LABEL[source]}
                    <span className={styles.count}>
                      {toolsBySource.get(source)?.length}
                    </span>
                  </div>
                  {(toolsBySource.get(source) ?? []).map((tool) => (
                    <ToolBlock key={tool.qualified_name} tool={tool} />
                  ))}
                </div>
              ))}
            </section>

            {/* The developer table-level data access folds in here (it used to
                be a separate "View data access" panel — consolidated into the
                inspector, since it's the same audience). */}
            <section className={styles.section}>
              <div className={styles.sectionHeader}>
                <h4>Data access (live tables)</h4>
              </div>
              <DataAccessReportView />
            </section>
          </>
        )}
      </div>
    </Modal>
  );
}
